import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { AnimatePresence, motion } from 'framer-motion'
import dayjs from 'dayjs'
import daLocale from 'dayjs/locale/da'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import { VictoryArea } from 'victory-area/es'
import { VictoryChart } from 'victory-chart/es'
import { VictoryContainer, VictoryLabel } from 'victory-core/es'
import { VictoryScatter } from 'victory-scatter/es'
import { VictoryAxis } from 'victory-axis/es'
import { VictoryVoronoiContainer } from 'victory-voronoi-container/es'
import { VictoryTooltip } from 'victory-tooltip/es'
import get from 'lodash/get'
import upperFirst from 'lodash/upperFirst'
import { datadogLogs } from '@datadog/browser-logs'

import Alert from '../Alert'
import Box from '../Box'
import { FallbackText } from '../TextElements'
import UsageComparisonWithProperty from '../UsageComparisonWithProperty'
import UsageComparisonWithTenancy from '../UsageComparisonWithTenancy'

import { MOON_GRAY, ORANGE } from '../config/colors'
import { formatUnit } from '../helpers/units'
import { dateString } from '../helpers/propTypes'
import { fetchUsageReport } from '../helpers/api'

dayjs.extend(advancedFormat)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)
dayjs.locale(daLocale)

const formatNumber = (number, maximumFractionDigits = 2) =>
  number.toLocaleString('da-DK', {
    minimumFractionDigits: 1,
    maximumFractionDigits,
  })

function getFractionDigitsByUnit(unit = '') {
  if (unit && unit.toLowerCase() === 'units') {
    return 1
  } else if (unit && unit.toLowerCase() === 'mwh') {
    return 3
  } else {
    return 2
  }
}

const sumPointsByDateFormat = (dateFormat) =>
  R.pipe(
    R.groupBy((point) => dayjs(point.time).format(dateFormat)),
    R.map(R.pipe(R.pluck('value'), R.sum)),
    R.toPairs,
    R.map(([key, val]) => ({ x: key, y: val })),
    R.sortBy(R.prop('x'))
  )

const NoDataFallback = () => (
  <FallbackText>Ingen data for perioden</FallbackText>
)

// Inner "dumb" component --------------------------------------------------- //
function UsageSummary({ className, currentPeriodUsage }) {
  const currentPeriodUsagePoints = get(
    currentPeriodUsage,
    ['usage', 'points'],
    []
  )
  const currentPeriodTotalUsageValue = get(
    currentPeriodUsage,
    ['totalUsage', 'value'],
    0
  )

  const maxiumFractionDigits = getFractionDigitsByUnit(
    get(currentPeriodUsage, ['totalUsage', 'unit'])
  )

  const formattedUnit = formatUnit(
    get(currentPeriodUsage, ['totalUsage', 'unit'])
  )

  const groupByDateFormat = getGroupByDateFormat(
    currentPeriodUsagePoints.length
  )

  const groupedData = sumPointsByDateFormat(groupByDateFormat)(
    currentPeriodUsagePoints
  )
  const hasPoints = currentPeriodUsagePoints.length > 0

  const warning = hasPoints
    ? getWarning(
        groupByDateFormat,
        R.pipe(R.head, R.prop('time'))(currentPeriodUsagePoints),
        R.pipe(R.last, R.prop('time'))(currentPeriodUsagePoints)
      )
    : null

  return (
    <div className={className}>
      {hasPoints ? (
        <table className="w-70 w-50-l mt4 center moon-gray f6">
          <tbody>
            <tr>
              <th scope="row" className="normal tl">
                {`${currentPeriodUsagePoints.length} dage:`}
              </th>
              <td className="tr nowrap">
                {`${formatNumber(
                  currentPeriodTotalUsageValue,
                  maxiumFractionDigits
                )}${formattedUnit}`}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <div className="tc mt4">
          <NoDataFallback />
        </div>
      )}
      {hasPoints && (
        <div>
          <div className="relative aspect-ratio--6x4">
            <VictoryChart
              containerComponent={
                // Avoid Safari error by only using voronoi container if there
                // are non-zero points (e.g. a non-zero total usage value)
                // https://github.com/wise-home/pippin/issues/38
                currentPeriodTotalUsageValue > 0 ? (
                  <VictoryVoronoiContainer />
                ) : (
                  <VictoryContainer />
                )
              }
              width={450}
              height={300}
              domainPadding={{ x: 0, y: 10 }}
              style={{
                parent: {
                  position: 'absolute',
                },
              }}
            >
              <VictoryAxis
                style={{
                  axis: {
                    stroke: MOON_GRAY,
                    strokeWidth: 0.5,
                  },
                  tickLabels: {
                    fill: MOON_GRAY,
                    fontSize: '12px',
                    fontFamily: 'inherit',
                  },
                }}
                tickFormat={(key) => {
                  switch (groupByDateFormat) {
                    case 'YYYY-MM':
                      return upperFirst(
                        dayjs(key).format('MMMM').substring(0, 3)
                      )
                    case 'YYYY-MM-DD':
                      return upperFirst(
                        dayjs(key).format('dddd').substring(0, 3)
                      )
                    case 'gggg-ww':
                      return `Uge ${R.pipe(R.split('-'), R.last)(key)}`
                    default:
                      return key
                  }
                }}
                tickLabelComponent={
                  <VictoryLabel
                    angle={270}
                    textAnchor="end"
                    verticalAnchor="middle"
                  />
                }
              />
              <VictoryAxis
                dependentAxis
                tickFormat={(t) => formatNumber(t, maxiumFractionDigits)}
                label={formattedUnit}
                style={{
                  axis: {
                    stroke: MOON_GRAY,
                    strokeWidth: 0.5,
                  },
                  grid: {
                    stroke: MOON_GRAY,
                    strokeWidth: 0.25,
                  },
                  axisLabel: {
                    fill: MOON_GRAY,
                    padding: 38,
                  },
                  tickLabels: {
                    fill: MOON_GRAY,
                    fontSize: '12px',
                    padding: 5,
                    fontFamily: 'inherit',
                  },
                }}
              />
              <VictoryArea
                animate={{ duration: 1000, onLoad: { duration: 0 } }}
                interpolation="step"
                data={groupedData}
                style={{
                  data: {
                    stroke: ORANGE,
                    fill: ORANGE,
                    fillOpacity: 0.3,
                  },
                }}
              />
              <VictoryScatter
                data={groupedData}
                style={{
                  data: {
                    fill: ORANGE,
                    fillOpacity: ({ active }) => (active ? 1 : 0),
                  },
                  labels: {
                    fill: '#fff',
                  },
                }}
                labels={({ datum }) => {
                  const formattedUsage =
                    formatNumber(datum.y, maxiumFractionDigits) + formattedUnit
                  const formattedDate = getLabelDateDescriptor(
                    groupByDateFormat,
                    datum.x
                  )
                  return formattedUsage + ' – ' + formattedDate
                }}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea
                    cornerRadius={2}
                    flyoutPadding={({ text }) =>
                      text.length > 1
                        ? { top: 0, bottom: 0, left: 18, right: 18 }
                        : 0
                    }
                    flyoutStyle={{
                      stroke: 'none',
                      fill: 'rgba(0, 0, 0, 0.7)',
                    }}
                  />
                }
              />
            </VictoryChart>
          </div>
        </div>
      )}
      <AnimatePresence>
        {warning && (
          <motion.div
            key="warning"
            initial={{
              opacity: 0.5,
              scaleY: 0.2,
            }}
            animate={{
              opacity: 1,
              scaleY: 1,
            }}
            exit={{
              opacity: 0.5,
              scaleY: 0.0,
            }}
          >
            <Alert type="warning" className="mt3" fontSizeClassName="f7">
              {`OBS: ${warning}`}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Remove oneOfType PropTypes.array if possible
const usageShape = PropTypes.oneOfType([
  PropTypes.shape({
    usage: PropTypes.shape({
      name: PropTypes.string,
      unit: PropTypes.string,
      points: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.number,
          time: dateString,
        })
      ),
    }),
    totalUsage: PropTypes.shape({
      time: dateString,
      value: PropTypes.number,
      unit: PropTypes.string,
    }),
  }),
  PropTypes.array,
])

UsageSummary.propTypes = {
  className: PropTypes.string,
  currentPeriodUsage: usageShape,
}

// Wrapping "smart" component ----------------------------------------------- //
export default function UsageSummaryContainer({
  title,
  icon,
  householdId,
  className,
  fromDate,
  toDate,
  type,
  waterType,
  children,
}) {
  const [currentPeriodUsage, setCurrentPeriodUsage] = useState()
  const [errorMessage, setErrorMessage] = useState()
  const [returnEmpty, setReturnEmpty] = useState(false)

  //const householdUsageProportion = currentPeriodUsage?.householdUsageProportion
  //const tenancyUsageProportion = currentPeriodUsage?.tenancyUsageProportion
  const householdUsageProportion = false
  const tenancyUsageProportion = false

  useEffect(() => {
    async function _loadUsage() {
      try {
        setErrorMessage(null)
        setReturnEmpty(false)
        const data = await fetchUsageReport({
          householdId,
          fromDate: new Date(fromDate),
          toDate: new Date(toDate),
          dataType: type,
          dataAttributes: waterType
            ? {
                'water-type': waterType,
              }
            : {},
        })
        setCurrentPeriodUsage(data)

        if (!data || get(data, ['usage', 'points'], []).length === 0) {
          setReturnEmpty(true)
        }
      } catch (error) {
        setErrorMessage(error.message)
        datadogLogs.logger.error(
          `UsageSummary.componentDidMount: ${error.message}`
        )
      }
    }
    const validDateRange = toDate >= fromDate

    if (validDateRange) {
      _loadUsage()
    } else {
      setErrorMessage('Den valgte periode er ugyldig')
    }

    //_loadUsage()
  }, [householdId, fromDate, toDate, type, waterType])

  return (
    <div className="ph2 ph3-l pb3 pb4-l w-100 w-50-ns w-50-print">
      <Box className="flex flex-column" title={title} icon={icon}>
        {errorMessage ? (
          <Alert type="error" className="mt4">
            {errorMessage}
          </Alert>
        ) : (
          [
            <UsageSummary
              key={`summary-${type}`}
              className={className}
              currentPeriodUsage={returnEmpty ? [] : currentPeriodUsage}
            />,
            children,

            householdUsageProportion && (
              <UsageComparisonWithProperty
                key={`comparison-property-${type}`}
                proportion={householdUsageProportion}
              />
            ),

            tenancyUsageProportion && (
              <UsageComparisonWithTenancy
                type={type}
                key={`comparison-tenancy-${type}`}
                proportion={tenancyUsageProportion}
              />
            ),
          ]
        )}
      </Box>
    </div>
  )
}

UsageSummaryContainer.propTypes = {
  type: PropTypes.oneOf([
    'electricity-usage-reports',
    'heat-usage-reports',
    'heat-cost-reports',
    'water-usage-reports',
  ]).isRequired,
  fromDate: dateString,
  toDate: dateString,
  waterType: PropTypes.oneOf(['hot', 'cold']),
  householdId: PropTypes.string,
}

function getGroupByDateFormat(diffDaysCount) {
  if (diffDaysCount <= 4 * 7) {
    return 'YYYY-MM-DD'
  } else if (diffDaysCount <= 12 * 7) {
    return 'gggg-ww'
  } else {
    return 'YYYY-MM'
  }
}

function getLabelDateDescriptor(groupByDateFormat, key) {
  switch (groupByDateFormat) {
    case 'YYYY-MM':
      return (
        upperFirst(dayjs(key).format('MMMM').substring(0, 3)) +
        ' ' +
        dayjs(key).format('YYYY')
      )
    case 'YYYY-MM-DD':
      return dayjs(key).format('DD.MM.YY')
    case 'gggg-ww':
      return `Uge ${R.pipe(R.split('-'), R.last)(key)}`
    default:
      return key
  }
}

function getWarning(groupByDateFormat, firstDate, lastDate) {
  if (!groupByDateFormat || !firstDate || !lastDate) {
    return null
  } else if (
    groupByDateFormat === 'gggg-ww' &&
    isNotAtPeriodStart(firstDate, 'week') &&
    isNotAtPeriodEnd(lastDate, 'week')
  ) {
    return 'Målinger er grupperet pr uge, men perioden starter og slutter ikke med fulde uger.'
  } else if (
    groupByDateFormat === 'gggg-ww' &&
    isNotAtPeriodStart(firstDate, 'week')
  ) {
    return 'Målinger er grupperet pr uge, men perioden starter ikke med en fuld uge.'
  } else if (
    groupByDateFormat === 'gggg-ww' &&
    isNotAtPeriodEnd(lastDate, 'week')
  ) {
    return 'Målinger er grupperet pr uge, men perioden slutter ikke med en fuld uge.'
  } else if (
    groupByDateFormat === 'YYYY-MM' &&
    isNotAtPeriodStart(firstDate, 'month') &&
    isNotAtPeriodEnd(lastDate, 'month')
  ) {
    return 'Målinger er grupperet pr måned, men perioden starter og slutter ikke med fulde måneder.'
  } else if (
    groupByDateFormat === 'YYYY-MM' &&
    isNotAtPeriodStart(firstDate, 'month')
  ) {
    return 'Målinger er grupperet pr måned, men perioden starter ikke med en fuld måned.'
  } else if (
    groupByDateFormat === 'YYYY-MM' &&
    isNotAtPeriodEnd(lastDate, 'month')
  ) {
    return 'Målinger er grupperet pr måned, men perioden slutter ikke med en fuld måned.'
  }
  return null
}

function isNotAtPeriodStart(date, period) {
  return dayjs(date).diff(dayjs(date).startOf(period), 'days') !== 0
}

function isNotAtPeriodEnd(date, period) {
  return dayjs(date).diff(dayjs(date).endOf(period), 'days') !== 0
}
